import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n";
import { scrollToId } from "@/lib/scroll";
import BrandMark from "@/components/BrandMark";

const LangToggle = ({ testid }) => {
  const { lang, setLang } = useLang();
  return (
    <div className="lang-toggle" data-testid={testid}>
      {["it", "en"].map((l) => (
        <button
          key={l}
          type="button"
          className={lang === l ? "active" : ""}
          onClick={() => setLang(l)}
          data-testid={`lang-toggle-${l}`}
          aria-pressed={lang === l}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("menu-open", open);
  }, [open]);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 400);
    } else {
      scrollToId(id);
    }
  };

  const links = [
    ["#metodo", t.nav.method, "nav-link-method"],
    ["#programmi", t.nav.programs, "nav-link-programs"],
    ["#community", t.nav.community, "nav-link-community"],
    ["#ecosistema", t.nav.ecosystem, "nav-link-ecosystem"],
    ["#faq", t.nav.faq, "nav-link-faq"],
  ];

  return (
    <header className={`site-header ${scrolled ? "scrolled" : ""}`} data-testid="site-header">
      <button className="brand" onClick={() => go("#top")} aria-label="Tech Training" data-testid="brand-logo">
        <BrandMark />
        <span className="brand-name">
          TECH <strong>TRAINING</strong>
        </span>
      </button>

      <button
        className="menu-toggle"
        type="button"
        aria-label={open ? t.nav.menuClose : t.nav.menuOpen}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        data-testid="menu-toggle"
      >
        <span></span>
        <span></span>
      </button>

      <nav className="site-nav" aria-label="Navigazione principale" data-testid="site-nav">
        {links.map(([id, label, testid]) => (
          <button key={id} className="nav-link" onClick={() => go(id)} data-testid={testid}>
            {label}
          </button>
        ))}
        <LangToggle testid="lang-toggle" />
        <button className="nav-cta" onClick={() => go("#contatti")} data-testid="header-cta">
          {t.nav.cta} <span aria-hidden="true">↗</span>
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            data-testid="mobile-menu"
          >
            {links.map(([id, label, testid]) => (
              <button key={id} className="nav-link" onClick={() => go(id)} data-testid={`mobile-${testid}`}>
                {label}
              </button>
            ))}
            <button className="nav-cta" onClick={() => go("#contatti")} data-testid="mobile-header-cta">
              {t.nav.cta} ↗
            </button>
            <LangToggle testid="mobile-lang-toggle" />
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
